import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { useToast } from "~/components/ui/use-toast";

import { ApiClient } from "../api-client";

interface ActivateSubscriptionRequest {
  token: string;
  userId: string;
  studentToken?: string;
}

interface ActivateSubscriptionResponse {
  success: boolean;
  message: string;
  subscriptionsCreated: number;
}

export function useActivateSubscription() {
  const { toast } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (
      options: ActivateSubscriptionRequest,
    ): Promise<ActivateSubscriptionResponse> => {
      const response = await ApiClient.instance.post<{ data: ActivateSubscriptionResponse }>(
        "/api/subscription/activate",
        options,
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: t("activation.success.title"),
          description:
            t("activation.success.description", { count: data.subscriptionsCreated }) +
            "Aby rozpocząć naukę, aktywuj konto klikając w link wysłany na e-mail.",
          variant: "default",
        });
      } else {
        toast({
          title: t("activation.error.title", "Błąd aktywacji"),
          description: data.message,
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      console.error("Subscription activation error:", error);

      if (error instanceof AxiosError) {
        const errorData = error.response?.data;

        return toast({
          title: t("activation.error.title", "Błąd aktywacji"),
          description:
            errorData?.message ||
            t("activation.error.generic", "Nie udało się aktywować subskrypcji"),
          variant: "destructive",
        });
      }

      toast({
        title: t("activation.error.title", "Błąd aktywacji"),
        description: t("common.error.somethingWentWrong", "Coś poszło nie tak"),
        variant: "destructive",
      });
    },
  });
}
